import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useCreditAudit, type CreditAuditEntry } from "@/hooks/use-credit-audit";
import { 
  Clock,
  User,
  Monitor,
  Package,
  Search,
  ArrowRight,
  Calendar,
  TrendingUp
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";

export const CreditAuditTrail = () => {
  const { data: auditEntries = [], isLoading } = useCreditAudit();
  const [searchTerm, setSearchTerm] = useState("");
  const [showAll, setShowAll] = useState(false);

  // Filter entries based on search term
  const filteredEntries = auditEntries.filter(entry => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      entry.device_id.toLowerCase().includes(searchLower) ||
      entry.device_name?.toLowerCase().includes(searchLower) ||
      entry.user_name?.toLowerCase().includes(searchLower) ||
      entry.user_email?.toLowerCase().includes(searchLower) ||
      entry.metadata?.package_name?.toLowerCase().includes(searchLower)
    );
  });

  // Show limited entries by default
  const displayEntries = showAll ? filteredEntries : filteredEntries.slice(0, 10);

  const getActionDisplay = (entry: CreditAuditEntry) => {
    switch (entry.action) {
      case 'manual_credit_allocation':
        return {
          text: 'allocated credits',
          icon: <TrendingUp className="h-4 w-4 text-green-600" />,
          color: 'text-green-600'
        };
      case 'user_credit_package_created':
        return {
          text: 'created user package',
          icon: <Package className="h-4 w-4 text-blue-600" />,
          color: 'text-blue-600'
        };
      case 'credit_package_created':
        return {
          text: 'created package',
          icon: <Package className="h-4 w-4 text-purple-600" />,
          color: 'text-purple-600'
        };
      default:
        return {
          text: entry.action.replace('_', ' '),
          icon: <Clock className="h-4 w-4 text-gray-600" />,
          color: 'text-gray-600'
        };
    }
  };

  const formatCreditsChange = (entry: CreditAuditEntry) => {
    const { metadata } = entry;
    if (!metadata) return null;

    if (metadata.credits_allocated) {
      return (
        <div className="flex items-center gap-2 text-sm">
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
            +{metadata.credits_allocated.toLocaleString()} credits
          </Badge>
        </div>
      );
    }

    if (metadata.previous_device_credits !== undefined && metadata.new_device_credits !== undefined) {
      const difference = metadata.new_device_credits - metadata.previous_device_credits;
      return (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">
            {metadata.previous_device_credits.toLocaleString()}
          </span>
          <ArrowRight className="h-3 w-3 text-muted-foreground" />
          <span className="font-medium">
            {metadata.new_device_credits.toLocaleString()}
          </span>
          {difference > 0 && (
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
              +{difference.toLocaleString()}
            </Badge>
          )}
        </div>
      );
    }

    return null;
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="text-muted-foreground">Loading audit trail...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-md font-medium flex items-center gap-2">
          <Clock className="h-4 w-4" />
          Credit Allocation History
        </h4>
        <Badge variant="outline">
          {auditEntries.length} total entries
        </Badge>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by device, user, or package..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {displayEntries.length === 0 ? (
        <Card>
          <CardContent className="text-center py-8">
            <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              {searchTerm ? 'No audit entries match your search.' : 'No credit allocation history found.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {displayEntries.map((entry) => {
            const actionDisplay = getActionDisplay(entry);
            const creditsChange = formatCreditsChange(entry);
            
            return (
              <Card key={entry.id} className="relative">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    {/* User Avatar Placeholder */}
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <User className="h-4 w-4 text-primary" />
                    </div>
                    
                    {/* Main Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="space-y-1">
                          {/* Action Summary */}
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm">
                              {entry.user_name || 'System'}
                            </span>
                            <span className="text-muted-foreground text-sm">
                              {actionDisplay.text}
                            </span>
                            {actionDisplay.icon}
                          </div>
                          
                          {/* Package Name */}
                          {entry.metadata?.package_name && (
                            <div className="flex items-center gap-1 text-sm text-muted-foreground">
                              <Package className="h-3 w-3" />
                              <span>{entry.metadata.package_name}</span>
                            </div>
                          )}
                          
                          {/* Device Info */}
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Monitor className="h-3 w-3" />
                            <span>
                              {entry.device_id === 'USER_OWNED' ? 
                                'User-Owned Credits' : 
                                `${entry.device_name || entry.device_id}`
                              }
                            </span>
                          </div>
                          
                          {/* Credits Change */}
                          {creditsChange}
                        </div>
                        
                        {/* Timestamp */}
                        <div className="text-right space-y-1 flex-shrink-0">
                          <div className="text-xs text-muted-foreground">
                            {formatDistanceToNow(new Date(entry.created_at), { addSuffix: true })}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {format(new Date(entry.created_at), 'MMM dd, HH:mm')}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          
          {/* Show More Button */}
          {!showAll && filteredEntries.length > 10 && (
            <div className="text-center">
              <Button 
                variant="outline" 
                onClick={() => setShowAll(true)}
                className="text-sm"
              >
                Show {filteredEntries.length - 10} more entries
              </Button>
            </div>
          )}
          
          {showAll && filteredEntries.length > 10 && (
            <div className="text-center">
              <Button 
                variant="outline" 
                onClick={() => setShowAll(false)}
                className="text-sm"
              >
                Show less
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};